/*
 * @Author: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @Date: 2025-07-07 14:48:17
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2025-09-03 11:51:21
 */
import React, { useEffect, useCallback, useRef, useState, ChangeEvent } from 'react'
import { Button, message } from 'antd'
import type { Key } from 'react'
import Filter from './components/Filter'
import RectifyTable from './components/rectifyTable'
import './index.scss'
import {
  fetchRectificationList,
  batchDeleteRectification,
  exportRectification,
  exportRectificationByIds,
  shareRectification
} from '../../actions/rectify'
import { setCreateRectifyDrawerVisible } from '../../slices/rectifySlice'
import parseQueryString from '../../utils/parseQueryString'
import { useAppDispatch, useAppSelector } from '../../app/hooks'

// 导入自定义hook
import useSelection from './hooks/useSelection'
import useRectifyData from './hooks/useRectifyData'
import useRectifyActions from './hooks/useRectifyActions'
import useRectifyAnalytics from './hooks/useRectifyAnalytics'
import { RectifyRecord } from './types'

const Rectify: React.FC = () => {
  const dispatch = useAppDispatch()
  const myRole = useAppSelector((state) => state.group.myRole)
  const userId = useAppSelector((state) => state.main.user?.user_id)
  const groupId = useAppSelector((state) => state.group.groupId)
  const members = useAppSelector((state) => state.group.members)
  const userList = members || []

  // 状态：从URL获取的recordNo，用于传递给Filter组件
  const [initialRecordNo, setInitialRecordNo] = useState<number | null>(null)

  // 使用埋点hook
  const { trackRectifyEvent } = useRectifyAnalytics()

  // 使用useSelection hook
  const {
    selectedIds,
    currentPageKeys,
    updateCurrentPageSelection,
    handleRowSelectionChange,
    handleSelectAllClick,
    isAllSelected,
    resetSelection,
    removeFromSelection
  } = useSelection()

  // 使用useRectifyData hook
  const {
    loading,
    data,
    pagination,
    filterParams,
    loadData,
    handleFilter: baseHandleFilter,
    handleTableChange
  } = useRectifyData({
    fetchRectificationList: (params) =>
      dispatch(fetchRectificationList(params)),
    updateCurrentPageSelection,
    groupId,
    userId: userId ?? null
  })

  // 使用useRectifyActions hook
  const {
    canBatchDelete,
    handleDeleteWithConfirm,
    handleShare,
    handleExport,
    handleDownload,
    handleRectify: originalHandleRectify
  } = useRectifyActions({
    batchDelete: (params) => dispatch(batchDeleteRectification(params)),
    shareRecord: (params) => dispatch(shareRectification(params)),
    exportData: (params) => dispatch(exportRectification(params)),
    exportByIds: (params) => dispatch(exportRectificationByIds(params)),
    loadData,
    resetSelection,
    removeFromSelection,
    selectedIds,
    data,
    filterParams,
    groupId,
    userId: userId ?? null
  })

  // 重写handleRectify以打开抽屉
  const handleRectify = () => {
    // 先执行原有的埋点逻辑
    originalHandleRectify()
    // 然后打开抽屉
    dispatch(setCreateRectifyDrawerVisible(true))
  }

  // 处理筛选，集成resetSelection
  const handleFilter = useCallback(
    (params: any, callback?: (data: any) => void) => {
      baseHandleFilter(params, resetSelection, callback)
    },
    [baseHandleFilter, resetSelection]
  )

  // 处理行选择，传入当前页记录，并添加埋点
  const handleRowSelectionChangeWithRecords = (newSelectedRowKeys: Key[]) => {
    handleRowSelectionChange(newSelectedRowKeys as (string | number)[], data.records)
    // 上报勾选记录的埋点
    trackRectifyEvent('clickBox', data)
  }

  // 处理全选点击，添加埋点
  const handleSelectAllWithTracking = (e: ChangeEvent<HTMLInputElement>, records: RectifyRecord[]) => {
    handleSelectAllClick(e, records)
    // 上报全选埋点
    trackRectifyEvent('clickAll', data)
  }

  // 处理URL中的recordID参数 - 只执行一次
  useEffect(() => {
    const handleRecordIdFromUrl = async () => {
      const { recordID } = parseQueryString()
      if (recordID && groupId && userId) {
        try {
          // 第一次调用：用recordID作为参数查询数据
          const params = {
            groupID: groupId,
            recordID: Number(recordID),
            pageSize: 10,
            pageNum: 1
          }

          const response = await dispatch(fetchRectificationList(params))

          if (response && response.records && response.records.length > 0) {
            // 获取到数据，提取recordNo
            const record = response.records[0]
            const recordNo = record.recordNo

            // 设置初始recordNo到状态中，供Filter组件使用
            setInitialRecordNo(recordNo)

            // 清除URL中的recordID参数
            const url = new URL(window.location.href)
            url.searchParams.delete('recordID')
            window.history.replaceState({}, '', url.toString())
          } else {
            // 未找到记录，提示已被删除
            message.warning('该整改任务不存在或已被删除')
          }
        } catch (error) {
          console.error('根据recordID定位记录失败:', error)
          message.error('加载整改任务失败')
        }
      }
    }

    // 延迟执行，确保页面初始化完成
    if (groupId && userId) {
      const { recordID } = parseQueryString()
      if (recordID) {
        trackRectifyEvent('enterPage', data, {
          fromPlace: '照片预览页'
        })
      } else {
        trackRectifyEvent('enterPage', data)
      }
      setTimeout(handleRecordIdFromUrl, 200)
    }
  }, [groupId, userId, dispatch, baseHandleFilter, resetSelection])

  // 保存loadData的ref
  const loadDataRef = useRef(loadData)
  loadDataRef.current = loadData

  // 监听整改任务创建成功事件，刷新列表数据
  useEffect(() => {
    const handleRectifyTaskCreated = () => {
      // 刷新当前页面数据，保持当前筛选条件和分页
      loadDataRef.current()
    }

    // 添加事件监听器
    window.addEventListener('rectifyTaskCreated', handleRectifyTaskCreated)

    // 组件卸载时移除事件监听器
    return () => {
      window.removeEventListener('rectifyTaskCreated', handleRectifyTaskCreated)
    }
  }, []) // 不依赖loadData，使用ref访问最新值

  return (
    <div className="rectify-page">
      <Filter
        onFilter={handleFilter}
        groupID={groupId}
        data={data}
        initialRecordNo={initialRecordNo}
      />

      <div className="task-summary">
        <span>{data.totalCount}条任务记录</span>
        <span className="task-status">
          未完成
          <span style={{ color: '#F54A45' }}>{data.unRectifiedCount}</span>
          条，已完成
          <span style={{ color: '#46B75B' }}>{data.rectifiedCount}</span>条
        </span>
        <div className="checkbox-wrapper">
          <input
            type="checkbox"
            id="selectAll"
            checked={isAllSelected(data.records)}
            onChange={(e) => handleSelectAllWithTracking(e, data.records)}
          />
          <label htmlFor="selectAll" style={{ color: '#000000D9' }}>
            全选当前页
          </label>
          {selectedIds.length > 0 && (
            <span className="selected-count">
              已选择 <strong>{selectedIds.length}</strong> 条
            </span>
          )}
        </div>
        <div className="action-buttons">
          <Button
            onClick={() => handleDeleteWithConfirm(null)}
            disabled={!canBatchDelete()}
            title={selectedIds.length === 0 ? '请先选择要删除的记录' : ''}
          >
            删除
          </Button>
          <Button onClick={handleRectify}>发起整改</Button>
          <Button onClick={handleDownload}>下载记录</Button>
          <Button type="primary" onClick={handleExport}>
            导出表格
          </Button>
        </div>
      </div>

      <RectifyTable
        data={data}
        loading={loading}
        pagination={pagination}
        handleTableChange={handleTableChange}
        selectedRowKeys={currentPageKeys}
        setSelectedRowKeys={handleRowSelectionChangeWithRecords}
        myRole={myRole}
        userId={userId ?? null}
        handleDelete={handleDeleteWithConfirm}
        handleShare={handleShare}
      />
    </div>
  )
}

export default Rectify
